import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Mail, Phone, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export default function PrivacyPolicyPage() {
  const effectiveDate = "February 1, 2026";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A0A0A] via-[#111] to-[#0A0A0A] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Privacy Policy & Terms of Service
          </h1>
          <p className="text-gray-400 mt-2">Effective Date: {effectiveDate}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Privacy Policy Section */}
        <Card className="card-dashboard">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#00E676]/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#00E676]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
            </div>

            <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
              <p className="text-sm text-gray-500">
                Effective Date: {effectiveDate}<br />
                Jurisdiction: United States – State of Georgia (Atlanta / Marietta)
              </p>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">1. Introduction</h3>
                <p>
                  Sraivest ("Company," "we," "us," or "our") is committed to protecting the privacy, security, and confidentiality of our customers and users ("you" or "your"). This Privacy Policy explains how we collect, use, disclose, and safeguard information in connection with our services.
                </p>
                <p>
                  By accessing or using Sraivest's platform, website, or services (collectively, the "Services"), you acknowledge that you have read and understand this Privacy Policy.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">2. Information We Collect</h3>
                <p>
                  We collect only the information necessary to provide and operate our Services in compliance with applicable laws and regulations.
                </p>
                
                <h4 className="text-lg font-medium text-white mt-4 mb-2">2.1 Personal Information</h4>
                <p>This may include, but is not limited to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Government-issued identification (where legally required)</li>
                  <li>Financial account information required for processing transactions</li>
                </ul>

                <h4 className="text-lg font-medium text-white mt-4 mb-2">2.2 Non-Personal Information</h4>
                <p>We may collect technical and usage data such as:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>IP address</li>
                  <li>Device and browser information</li>
                  <li>Log files and usage analytics</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">3. Use of Information</h3>
                <p>We use collected information solely to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Provide, maintain, and improve our Services</li>
                  <li>Process transactions and service fees</li>
                  <li>Comply with legal, regulatory, and compliance obligations</li>
                  <li>Prevent fraud, unauthorized activity, or misuse of the platform</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">4. Data Sharing and Disclosure</h3>
                <p><strong className="text-[#00E676]">Sraivest does not sell, rent, or trade customer data.</strong></p>
                <p>We may share information only under the following circumstances:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>With licensed brokers, regulated financial institutions, and service providers strictly as necessary to deliver Services</li>
                  <li>To comply with applicable laws, regulations, subpoenas, or legal processes</li>
                  <li>To protect the rights, safety, and integrity of Sraivest, its users, or the public</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">5. Customer Funds and Financial Responsibility</h3>
                <p>
                  <strong className="text-[#F7931A]">Sraivest does not take custody or ownership of customer funds.</strong> All funds remain the sole property and responsibility of the customer.
                </p>
                <p>
                  Sraivest earns only a disclosed service fee and processing fee, and no additional compensation beyond these fees.
                </p>
                <p>
                  Customer funds are held with FDIC-insured financial institutions, subject to applicable coverage limits and regulatory requirements.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">6. Data Security</h3>
                <p>
                  We implement commercially reasonable administrative, technical, and physical safeguards designed to protect customer information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">7. Data Retention</h3>
                <p>
                  We retain customer information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy or as required by law.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">8. Your Rights</h3>
                <p>
                  Depending on your jurisdiction, you may have rights to access, correct, delete, or restrict the processing of your personal information, subject to legal and regulatory obligations.
                </p>

                <h4 className="text-lg font-medium text-white mt-4 mb-2">8.1 GDPR Rights (EU Users)</h4>
                <p>
                  If you are located in the European Union, you have rights under the General Data Protection Regulation (GDPR), including the right to access, rectification, erasure, data portability, and objection to processing. We process EU personal data based on legitimate interests, contractual necessity, and legal compliance.
                </p>

                <h4 className="text-lg font-medium text-white mt-4 mb-2">8.2 CCPA Rights (California Residents)</h4>
                <p>
                  California residents have rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, the right to request deletion, and the right to opt out of the sale of personal information. <strong>Sraivest does not sell personal data.</strong>
                </p>
                <p>Requests may be submitted using the contact information listed below.</p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">9. Changes to This Privacy Policy</h3>
                <p>
                  We reserve the right to modify this Privacy Policy at any time. Updates will be effective upon posting, with the revised effective date indicated above.
                </p>
              </section>
            </div>
          </CardContent>
        </Card>

        {/* Terms of Service Section */}
        <Card className="card-dashboard">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#6366F1]/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#6366F1]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Terms of Service</h2>
            </div>

            <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
              <p className="text-sm text-gray-500">Effective Date: {effectiveDate}</p>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">1. Acceptance of Terms</h3>
                <p>
                  By accessing or using Sraivest's Services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you must not use the Services.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">2. Eligibility</h3>
                <p>
                  You must be legally capable of entering into a binding contract and comply with all applicable laws and regulations to use the Services.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">3. Services Description</h3>
                <p>
                  Sraivest operates as a technology platform and also works in connection with registered and licensed financial entities, where applicable. Sraivest itself does not provide personalized investment advice unless explicitly stated in a separate written agreement.
                </p>
                <p>
                  The platform facilitates access to licensed brokers and regulated financial services for both retail and accredited users, subject to eligibility and regulatory requirements.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">4. Fees</h3>
                <p>
                  Sraivest charges only a <strong className="text-[#00E676]">service fee</strong> and a <strong className="text-[#00E676]">processing fee</strong>, which are disclosed prior to transaction completion. No other fees are charged by Sraivest.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">5. Customer Responsibility</h3>
                <p>You acknowledge and agree that:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>All investment and financial decisions are made at your own discretion and risk</li>
                  <li>You are solely responsible for your funds, transactions, and compliance with applicable laws</li>
                  <li>Market performance, returns, or outcomes are not guaranteed</li>
                </ul>
              </section>

              <section className="bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-lg p-4">
                <h3 className="text-xl font-semibold text-[#F7931A] mb-3">5A. Risk Disclosure</h3>
                <p>
                  <strong>Investing involves risk, including the possible loss of principal.</strong> Past performance is not indicative of future results. Market conditions, economic factors, and regulatory changes may impact outcomes. You should carefully evaluate your financial situation and risk tolerance before engaging in any financial activity.
                </p>
              </section>

              <section className="bg-[#6366F1]/10 border border-[#6366F1]/30 rounded-lg p-4">
                <h3 className="text-xl font-semibold text-[#6366F1] mb-3">5B. No Investment Advice</h3>
                <p>
                  Nothing on the Sraivest platform constitutes financial, legal, tax, or investment advice. Any information provided is for general informational purposes only and should not be relied upon as a recommendation. You are encouraged to consult with licensed professionals before making investment decisions.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">6. No Custody of Funds</h3>
                <p>
                  Sraivest does not hold, control, or insure customer funds directly. Funds are maintained with FDIC-insured financial institutions, subject to regulatory limits.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">7. Third-Party Services</h3>
                <p>
                  Sraivest may integrate or rely on third-party brokers and service providers. We are not responsible for the acts, omissions, or performance of third parties.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">8. Intellectual Property</h3>
                <p>
                  All content, trademarks, and materials provided through the Services are the property of Sraivest or its licensors and may not be used without written permission.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">9. Limitation of Liability</h3>
                <p>
                  To the maximum extent permitted by law, Sraivest shall not be liable for any indirect, incidental, consequential, or special damages arising from use of the Services.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">10. Indemnification</h3>
                <p>
                  You agree to indemnify and hold harmless Sraivest from claims arising from your use of the Services or violation of these Terms.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">11. Termination</h3>
                <p>
                  We reserve the right to suspend or terminate access to the Services at our discretion, subject to applicable law.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">12. Governing Law</h3>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the <strong>State of Georgia, United States</strong>, with venue in Atlanta or Marietta, Georgia, without regard to conflict of law principles.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mt-6 mb-3">13. Changes to Terms</h3>
                <p>
                  We may update these Terms at any time. Continued use of the Services constitutes acceptance of the revised Terms.
                </p>
              </section>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="card-dashboard">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
            <p className="text-gray-400 mb-6">
              For questions or concerns regarding this Privacy Policy or Terms of Service, please contact:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-white/5">
                <Mail className="w-5 h-5 text-[#00E676]" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <a href="mailto:sparkxtvproductions@gmail.com" className="text-white hover:text-[#00E676]">
                    sparkxtvproductions@gmail.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-4 rounded-lg bg-white/5">
                <Phone className="w-5 h-5 text-[#6366F1]" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <a href="tel:720-523-3530" className="text-white hover:text-[#6366F1]">
                    720-523-3530
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-4 rounded-lg bg-white/5">
                <Globe className="w-5 h-5 text-[#F7931A]" />
                <div>
                  <p className="text-sm text-gray-500">Website</p>
                  <a href="https://www.sraivest.market" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#F7931A]">
                    www.sraivest.market
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-4 rounded-lg bg-white/5">
                <Globe className="w-5 h-5 text-[#00E676]" />
                <div>
                  <p className="text-sm text-gray-500">Additional Contact</p>
                  <a href="https://www.sparkxtv.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#00E676]">
                    www.sparkxtv.com
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-[#00E676]/10 to-[#6366F1]/10 border border-[#00E676]/20 text-center">
              <p className="text-xl font-semibold text-white">
                Thank you for your business.
              </p>
              <p className="text-2xl font-bold text-[#00E676] mt-2">
                Welcome to a life of wealth.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back to top */}
        <div className="text-center pb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#00E676] hover:underline">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
